import { supabase } from '../../../lib/supabaseClient';
import { CreateOfferLetterPayload, BulkOfferResponse, GetUploadUrlsPayload, GetUploadUrlResponseItem } from '../types';
import { ApiResponse } from '../../../types/api';
import { handleEdgeFunctionError } from '../../../utils/edgeFunctionUtils';

export async function createOfferLetterWithEdgeFunction(payload: CreateOfferLetterPayload[]): Promise<ApiResponse<BulkOfferResponse>> {
  try {
    const { data, error } = await supabase.functions.invoke('create-offer-letter', {
      body: payload,
    });

    if (error) {
      console.error('[Edge Function Error] createOfferLetter:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data ?? data ?? null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] createOfferLetter:', err);
    return { data: null, error: err };
  }
}

export async function getOfferPdfWithEdgeFunction(applicationId: string): Promise<{ blob: Blob; filename: string }> {
  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  try {
    const { data, error } = await supabase.functions.invoke('get-offer-pdf', {
      body: { application_id: applicationId },
      responseType: 'arraybuffer',
    } as any);

    if (error) {
      const extractedError = await handleEdgeFunctionError(error);
      throw extractedError;
    }

    if (!data) {
      throw new Error('No PDF data received');
    }

    return {
      blob: new Blob([data], { type: 'application/pdf' }),
      filename: `offer_${applicationId}.pdf`,
    };
  } catch (error) {
    console.error('[Edge Function Error] get-offer-pdf:', error);
    throw error;
  }
}

export async function downloadOfferPdfWithEdgeFunction(applicationId: string): Promise<{ blob: Blob; filename: string }> {
  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  try {
    const { data, error } = await supabase.functions.invoke('download-offer-pdf', {
      body: { application_id: applicationId },
      responseType: 'arraybuffer',
    } as any);

    if (error) {
      const extractedError = await handleEdgeFunctionError(error);
      throw extractedError;
    }

    if (!data) {
      throw new Error('No PDF data received');
    }

    return {
      blob: new Blob([data], { type: 'application/pdf' }),
      filename: `offer_${applicationId}.pdf`,
    };
  } catch (error) {
    console.error('[Edge Function Error] download-offer-pdf:', error);
    throw error;
  }
}

export async function getUploadUrlsWithEdgeFunction(payload: GetUploadUrlsPayload): Promise<ApiResponse<GetUploadUrlResponseItem[]>> {
  try {
    const { data, error } = await supabase.functions.invoke('get-upload-urls', {
      body: payload,
    });

    if (error) {
      console.error('[Edge Function Error] getUploadUrls:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data ?? data ?? null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] getUploadUrls:', err);
    return { data: null, error: err };
  }
}

export async function uploadOfferPdfToSignedUrl(
  path: string,
  token: string,
  file: File
): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from('offers')
      .uploadToSignedUrl(path, token, file);

    if (error) {
      throw new Error(`Upload failed: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error('[Upload Error] uploadOfferPdfToSignedUrl:', error);
    return false;
  }
}

export async function triggerOfferPdfDownload(applicationId: string): Promise<void> {
  const { blob, filename } = await downloadOfferPdfWithEdgeFunction(applicationId);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}
